<template>
  <Modal ref="modal" :title="modalTitle" size="large" @close="handleClose">
    <div v-if="loading" class="flex items-center justify-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
    </div>
    
    <div v-else-if="versions.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
      No versions available for this song.
    </div>
    
    <div v-else class="space-y-2">
      <div v-for="version in versions" :key="version.id" 
           class="flex items-center p-3 rounded-lg border transition-colors cursor-pointer"
           :class="isVersionSelected(version) 
             ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 dark:border-indigo-600' 
             : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'"
           @click="applyVersion(version)">
        
        <div class="flex-shrink-0 mr-3">
          <input type="radio" 
                 :name="'version-' + songId" 
                 :value="version.id"
                 :checked="isVersionSelected(version)"
                 class="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 focus:ring-indigo-500 dark:focus:ring-indigo-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 pointer-events-none">
        </div>
        
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
            {{ version.name }}
          </p>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            {{ formatDate(version.updatedAt || version.createdAt) }}
          </p>
        </div>
        
        <div class="flex-shrink-0 ml-2">
          <button @click.stop="openPreview(version)" 
                  class="p-1.5 rounded-md text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                  title="Preview version">
            <EyeIcon class="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  </Modal>
  
  <!-- Version Preview Modal -->
  <Modal ref="previewModal" :title="previewTitle" size="fullscreen" class="!z-[300]">
    <div v-if="previewVersion" class="flex flex-col items-center w-full">
      <div v-html="previewVersion.content" 
           class="lyric-content text-base leading-6 rounded outline-none md:px-4 max-w-4xl w-full music-map">
      </div>
    </div>
  </Modal>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, defineExpose } from 'vue'
import Modal from '@/components/common/TriadModal.vue'
import { EyeIcon } from '@heroicons/vue/24/outline'
import songService from '@/service/song.service'

const props = defineProps({
  songId: {
    type: String,
    required: true
  },
  songTitle: {
    type: String,
    default: ''
  },
  currentContent: {
    type: String,
    default: ''
  },
  currentVersionId: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['select', 'close'])

const modal = ref(null)
const previewModal = ref(null)
const loading = ref(false)
const versions = ref([])
const previewVersion = ref(null)

const modalTitle = computed(() => `Versions - ${props.songTitle}`)
const previewTitle = computed(() => `Preview - ${previewVersion.value?.name || 'Version'}`)

// Check if a version is currently selected
const isVersionSelected = (version) => {
  if (!props.currentVersionId) {
    return version.id === 'original'
  }
  return props.currentVersionId === version.id
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
}

const fetchVersions = async () => {
  if (!props.songId) {
    return
  }
  
  loading.value = true
  try {
    const response = await songService.fetchSongVersions(props.songId)
    if (response.ok) {
      const data = await response.json()
      const fetchedVersions = Array.isArray(data) ? data : []
      
      // Add original version at the top of the list
      const originalVersion = {
        id: 'original',
        name: 'Original',
        content: props.currentContent,
        createdAt: null,
        updatedAt: null
      }
      
      versions.value = [originalVersion, ...fetchedVersions]
    }
  } catch (error) {
    console.error('Error fetching versions:', error)
  } finally {
    loading.value = false
  }
}

const applyVersion = async (version) => {
  if (isVersionSelected(version)) {
    return
  }
  
  if (version.id === 'original') {
    loading.value = true
    try {
      const response = await songService.fetchSong(props.songId)
      if (response.ok) {
        const songData = await response.json()
        const originalContent = songData.content || songData.lyric || ''
        emit('select', {
          versionId: 'original',
          content: originalContent,
          name: 'Original',
          media: songData.media,
          tags: songData.tags,
          artist: songData.artist,
          composers: songData.composers,
          title: songData.title
        })
        handleClose()
      }
    } catch (error) {
      console.error('Error fetching original song:', error)
    } finally {
      loading.value = false
    }
  } else {
    loading.value = true
    try {
      const response = await songService.fetchSong(props.songId)
      if (response.ok) {
        const songData = await response.json()
        emit('select', {
          versionId: version.id,
          content: version.content,
          name: version.name,
          media: (version.media && version.media.length > 0) ? version.media : songData.media,
          tags: version.effective_tags || version.tags || songData.tags,
          artist: songData.artist,
          composers: songData.composers,
          title: songData.title
        })
        handleClose()
      }
    } catch (error) {
      console.error('Error fetching original song for version:', error)
    } finally {
      loading.value = false
    }
  }
}

const openPreview = async (version) => {
  if (version.id === 'original' && !version.contentLoaded) {
    loading.value = true
    try {
      const response = await songService.fetchSong(props.songId)
      if (response.ok) {
        const songData = await response.json()
        version.content = songData.content || songData.lyric || ''
        version.contentLoaded = true
      }
    } catch (error) {
      console.error('Error fetching original song:', error)
    } finally {
      loading.value = false
    }
  }
  previewVersion.value = version
  previewModal.value?.open()
}

const handleClose = () => {
  emit('close')
  modal.value?.close()
}

const open = () => {
  fetchVersions()
  modal.value?.open()
}

defineExpose({ open })
</script>
